import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, ExternalLink, Linkedin, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { verifyAPI } from '@/lib/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function VerificationLinkDialog({ productId, productName, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open || !productId) return;
    setLoading(true);
    setToken(null);
    verifyAPI
      .issueToken(productId, 30)
      .then((r) => {
        setToken(r.data.token);
        setMeta(r.data);
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || 'Failed to issue verification link.';
        toast.error(msg);
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, productId]);

  const publicUrl = token ? `${BACKEND}/api/verify/${productId}?token=${token}&format=html` : '';
  const jsonUrl = token ? `${BACKEND}/api/verify/${productId}?token=${token}` : '';
  const qrUrl = token ? `${BACKEND}/api/verify/${productId}/qr?token=${token}` : '';
  const linkedInShare = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`;

  const copy = (text, label = 'Link') => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="verification-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-600" />
            Veridex Verified · Public verification link
          </DialogTitle>
          <DialogDescription>
            Share this state-bound, signed link with auditors or customers. The link expires in 30 days and becomes invalid the moment any field of <strong>{productName || 'this product'}</strong> changes.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground" data-testid="verification-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Generating cryptographic token…
          </div>
        )}

        {!loading && token && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <img
                src={qrUrl}
                alt="Verification QR code"
                width={180}
                height={180}
                className="rounded-lg border bg-white p-2"
                data-testid="verification-qr"
              />
            </div>

            <div className="rounded-md border bg-muted/40 p-3 text-xs break-all font-mono" data-testid="verification-public-url">
              {publicUrl}
            </div>

            {meta?.state_hash && (
              <div className="text-xs text-muted-foreground">
                State hash: <span className="font-mono">{meta.state_hash}</span> · TTL: {meta.ttl_days || 30} days
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => copy(publicUrl, 'Verification URL')} size="sm" data-testid="copy-verify-url-btn">
                <Copy className="w-4 h-4 mr-1.5" /> Copy URL
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="open-verify-url-btn">
                  <ExternalLink className="w-4 h-4 mr-1.5" /> Open
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href={linkedInShare} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin-btn">
                  <Linkedin className="w-4 h-4 mr-1.5" /> Share on LinkedIn
                </a>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => copy(jsonUrl, 'Auditor JSON URL')}>
                <Copy className="w-4 h-4 mr-1.5" /> Copy JSON URL
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
